// One open conversation: header (title/rename/delete/confirm-writes),
// message timeline (live via useChatStream) and the composer. Shared by the
// app-shell slide-out panel and the full /chat/$conversationId page.

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ChatMessage } from '@goblic/shared'
import { mergeChatMessages } from '../../lib/chat-messages'
import { chatApi } from '../../lib/api-client'
import { useLookups } from '../../hooks/useLookups'
import { useChatStream } from '../../hooks/useChatStream'
import { ErrorNote, LoadingRows } from '../ui'
import { MessageList } from './message-list'
import { ChatComposer } from './chat-composer'

export function ConversationView({
  conversationId,
  pageContext,
  onDeleted,
  layout = 'panel',
}: {
  conversationId: string
  /** Where it's shown: the narrow side panel or the full /chat page (picks the portrait or wide background scene). */
  layout?: 'panel' | 'page'
  /** The page the chat was opened from (07-chat.md §2.4 "pageContext") — e.g. `{ projectId }`. */
  pageContext?: Record<string, unknown>
  onDeleted?: () => void
}) {
  const lookups = useLookups()
  const queryClient = useQueryClient()
  const [editingTitle, setEditingTitle] = useState(false)
  const [olderMessages, setOlderMessages] = useState<ChatMessage[]>([])
  const [olderCursor, setOlderCursor] = useState<string | null | undefined>(undefined)
  const [compactQueued, setCompactQueued] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')

  const stream = useChatStream(conversationId)
  const conversationQuery = useQuery({
    queryKey: ['chat-conversation', conversationId],
    queryFn: () => chatApi.getConversation(conversationId),
    refetchOnWindowFocus: false,
    refetchInterval: stream.status === 'error' ? 5_000 : false,
  })

  const statusQuery = useQuery({
    queryKey: ['chat-status', conversationId],
    queryFn: () => chatApi.getStatus(conversationId),
    refetchInterval: 5_000,
  })
  const loadOlder = useMutation({
    mutationFn: (before: string) => chatApi.getConversation(conversationId, before),
    onSuccess: (page) => {
      if (page.conversation.id !== conversationId) return
      setOlderMessages((previous) => mergeChatMessages(page.messages, previous))
      setOlderCursor(page.nextCursor)
    },
  })
  const compact = useMutation({
    mutationFn: () => chatApi.compact(conversationId),
    onSuccess: () => setCompactQueued(true),
  })
  useEffect(() => {
    setOlderMessages([])
    setOlderCursor(undefined)
    setCompactQueued(false)
  }, [conversationId])

  // Keep previously visible rows when a newer page replaces the initial page.
  useEffect(() => {
    if (!conversationQuery.data) return
    setOlderMessages((previous) => mergeChatMessages(previous, conversationQuery.data!.messages))
    setOlderCursor((previous) => previous === undefined ? conversationQuery.data!.nextCursor : previous)
  }, [conversationQuery.data])
  useEffect(() => {
    if (statusQuery.data?.conversation.status === 'running') setCompactQueued(false)
  }, [statusQuery.data?.conversation.status])

  // SSE owns messages. Refresh one bounded page on reconnect to reconcile a missed burst.
  useEffect(() => {
    if (stream.status === 'open') void queryClient.invalidateQueries({ queryKey: ['chat-conversation', conversationId] })
  }, [stream.status, queryClient, conversationId])

  const rename = useMutation({
    mutationFn: (title: string) => chatApi.updateConversation(conversationId, { title }),
    onSuccess: () => {
      setEditingTitle(false)
      void queryClient.invalidateQueries({ queryKey: ['chat-status', conversationId] })
      void queryClient.invalidateQueries({ queryKey: ['chat-conversations'] })
    },
  })

  const toggleConfirmWrites = useMutation({
    mutationFn: (confirmWrites: boolean) => chatApi.updateConversation(conversationId, { confirmWrites }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chat-status', conversationId] }),
  })

  const remove = useMutation({
    mutationFn: () => chatApi.deleteConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-conversations'] })
      onDeleted?.()
    },
  })

  // 07 QA #5's stop button — the worker notices within CHAT_CANCEL_POLL_MS
  // and stops the runner itself; this just records the request, so the
  // turn stays "running" (and this button disabled) until that happens.
  const cancel = useMutation({
    mutationFn: (turnId: string) => chatApi.cancelTurn(conversationId, turnId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chat-status', conversationId] }),
  })

  if (conversationQuery.isLoading) return <LoadingRows label="Loading conversation…" />
  if (conversationQuery.isError) return <ErrorNote error={conversationQuery.error} what="this conversation" />
  const conversation = statusQuery.data?.conversation ?? conversationQuery.data!.conversation
  const summary = statusQuery.data?.summary ?? conversationQuery.data!.summary
  const nextCursor = olderCursor === undefined ? conversationQuery.data!.nextCursor : olderCursor
  const storedMessages = conversationQuery.data!.messages

  const messages = mergeChatMessages(olderMessages, storedMessages, stream.messages)
    .filter((message) => message.conversationId === conversationId)

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3.5 py-2.5">
        <div className="min-w-[10rem] flex-1">
          {editingTitle ? (
            <input
              type="text"
              autoFocus
              className="field-input"
              value={titleDraft}
              onChange={(event) => setTitleDraft(event.target.value)}
              onBlur={() => rename.mutate(titleDraft.trim() || 'Untitled conversation')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') (event.target as HTMLInputElement).blur()
                if (event.key === 'Escape') setEditingTitle(false)
              }}
            />
          ) : (
            <button
              type="button"
              className="truncate text-left font-display text-md font-semibold text-ink hover:underline"
              onClick={() => {
                setTitleDraft(conversation.title)
                setEditingTitle(true)
              }}
            >
              {conversation.title || 'Untitled conversation'}
            </button>
          )}
          <p className="truncate text-sm text-ink-3">
            {(lookups.connections.find((c) => c.id === conversation.credentialId)?.name ?? 'No connection')}
            {conversation.model ? ` · ${conversation.model}` : ''}
          </p>
        </div>
        {/* `ml-auto` keeps this group right-aligned whether it shares the
            header's first row with the title or — too little room for both,
            e.g. the slide-out panel's own width — wraps onto its own row
            below it (the parent's `flex-wrap`, the title's `min-w-[10rem]` floor
            is what forces that wrap instead of squeezing the title away). */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-ink-3">
            <input
              type="checkbox"
              role="switch"
              className="switch"
              checked={conversation.confirmWrites}
              disabled={toggleConfirmWrites.isPending}
              onChange={(event) => toggleConfirmWrites.mutate(event.target.checked)}
            />
            Confirm changes
          </label>
          <button
            type="button"
            className="btn-ghost btn-sm shrink-0"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm('Delete this conversation? This can’t be undone.')) remove.mutate()
            }}
          >
            Delete
          </button>
        </div>
      </div>

      <div className={`chat-scene chat-scene-${layout} flex min-h-0 flex-1 flex-col`}>
        {summary ? <p className="px-4 pt-3 text-sm text-ink-3">Earlier messages summarized for the assistant. Full history remains available.</p> : null}
        <div className="flex flex-wrap gap-2 px-4 pt-2">
          {nextCursor ? <button className="btn-ghost btn-sm" disabled={loadOlder.isPending} onClick={() => loadOlder.mutate(nextCursor)}>{loadOlder.isPending ? 'Loading…' : 'Load earlier messages'}</button> : null}
          <button className="btn-ghost btn-sm" disabled={conversation.status === 'running' || compact.isPending || compactQueued} onClick={() => compact.mutate()}>{compactQueued ? 'Will summarize before your next reply' : 'Summarize earlier turns on next reply'}</button>
        </div>
        {loadOlder.isError ? <ErrorNote error={loadOlder.error} what="earlier messages" /> : null}
        {compact.isError ? <ErrorNote error={compact.error} what="compaction" /> : null}
        <MessageList
          conversationId={conversationId}
          messages={messages}
          waitingForRunner={conversation.status === 'running'}
        />
      </div>

      <ChatComposer
        conversationId={conversationId}
        disabled={conversation.status === 'running'}
        pageContext={pageContext}
        activeTurnId={conversation.activeTurnId}
        cancelRequested={conversation.cancelRequested}
        onStop={(turnId) => cancel.mutate(turnId)}
        stopping={cancel.isPending}
      />
    </div>
  )
}
